import * as PIXI from 'pixi.js';
import { gsap } from 'gsap';
import { APP_WIDTH, APP_HEIGHT } from '../../utils';
import { playPrefabClip, preloadPrefabClips } from '../../animations/prefab';
import { starterDeckSelectPrefab } from '../../animations/prefabs/starterDeckSelect';

import Tappable from '../../common/containers/Tappable';
import TappableGraphicsButton from '../../common/containers/TappableGraphicsButton';

const DECK_HEIGHT = 1100;
const SIDE_X = 550;
const SIDE_SCALE = 0.6;
const SIDE_ALPHA = 0.9;
const BUTTON_WIDTH = 470;
const BUTTON_HEIGHT = 150;

const PREVIEW_ITEMS = [
    { id: 'starter-ST01', name: 'ST-01 Deck', deckName: 'Straw Hat Crew', description: 'A deck featuring Character cards with [Rush] effects. Take down your opponent with quick attacks!', image: '/assets/shop/st01_for_shop.png', canClaim: true },
    { id: 'starter-ST02', name: 'ST-02 Deck', deckName: 'Worst Generation', description: 'A deck made for fighting offensively and defensively using DON!! cards. Defeat your opponent with the ultimate offense-defense strategy!', image: '/assets/shop/st02_for_shop.png', canClaim: true },
    { id: 'starter-ST03', name: 'ST-03 Deck', deckName: 'The Seven Warlords of the Sea', description: 'A deck featuring Character cards with [Blocker] effects. Triumph by taking control over your opponent\'s movements!', image: '/assets/shop/st03_for_shop.png', canClaim: true },
    { id: 'starter-ST04', name: 'ST-04 Deck', deckName: 'Animal Kingdom Pirates', description: 'A deck featuring Character cards with powerful DON!!-based effects Use your DON!! cards to overwhelm your opponent with hard-hitting effects!', image: '/assets/shop/st04_for_shop.png', canClaim: true },
];

const findText = (node) => {
    if (node instanceof PIXI.Text) return node;
    for (const child of node.children || []) {
        const found = findText(child);
        if (found) return found;
    }
    return null;
};

export default class StarterDeckSelectContainer extends Tappable {
    static previewItems() {
        return PREVIEW_ITEMS;
    }

    static createPreview() {
        return new StarterDeckSelectContainer(PREVIEW_ITEMS, APP_WIDTH, APP_HEIGHT);
    }

    static async loadItemAssets(items) {
        const images = (items || [])
            .filter((item) => item.image && !PIXI.Assets.get(item.image))
            .map((item) => ({ src: item.image }));
        await Promise.all([
            images.length > 0 ? PIXI.Assets.load(images) : null,
            preloadPrefabClips(starterDeckSelectPrefab),
        ]);
    }

    constructor(items = [], width = APP_WIDTH, height = APP_HEIGHT) {
        super({ shouldStopEventPropagation: true });

        this.label = 'starterDeckSelectContainer';
        this.items = items || [];
        this.index = 0;
        this.claimingItemId = null;
        this.isAnimating = false;
        this.layoutVersion = 0;
        this.slideTimeline = null;
        this.onSwipeLeft = () => this.slide(1);
        this.onSwipeRight = () => this.slide(-1);
        this.buildMainLayout(width, height);
    }

    itemAt(offset) {
        if (this.items.length === 0) return null;
        return this.items[(this.index + offset + this.items.length) % this.items.length];
    }

    currentItem() {
        return this.itemAt(0);
    }

    setItems(items) {
        this.items = items || [];
        this.index = Math.min(this.index, Math.max(this.items.length - 1, 0));
        if (this.centerDeck) this.placeDecks();
    }

    setClaimingItemId(itemId) {
        this.claimingItemId = itemId;
        this.updateClaimButton();
    }

    async playClip(name) {
        const playback = playPrefabClip(starterDeckSelectPrefab, name, this, {
            keepFinal: true,
            createTimeline: () => {
                const timeline = gsap.timeline();
                this.slideTimeline = timeline;
                return timeline;
            },
        });
        return playback;
    }

    async slide(step) {
        if (this.isAnimating || this.claimingItemId != null || this.items.length < 2) return;

        this.isAnimating = true;
        const version = this.layoutVersion;
        const forward = step > 0;
        this.prevPrevDeck.zIndex = 0;
        this.nextNextDeck.zIndex = 0;
        this.centerDeck.zIndex = 2;
        this.leftDeck.zIndex = forward ? 1 : 3;
        this.rightDeck.zIndex = forward ? 3 : 1;

        try {
            await this.playClip(forward ? 'slide-left' : 'slide-right');
            if (version !== this.layoutVersion) return;

            this.index = (this.index + step + this.items.length) % this.items.length;
            this.placeDecks();
        } finally {
            if (version === this.layoutVersion) this.isAnimating = false;
        }
    }

    setSlot(slot, item, x, scale, alpha, zIndex) {
        slot.removeChildren();
        slot.position.set(x, 0);
        slot.scale.set(scale);
        slot.alpha = alpha;
        slot.zIndex = zIndex;
        slot.visible = Boolean(item);

        const texture = item?.image && PIXI.Assets.get(item.image);
        if (!texture) {
            slot.hitArea = null;
            return;
        }

        const sprite = new PIXI.Sprite(texture);
        sprite.anchor.set(0.5);
        const spriteScale = DECK_HEIGHT / texture.height;
        sprite.scale.set(spriteScale);
        sprite.eventMode = 'none';
        slot.addChild(sprite);

        if (slot.eventMode !== 'none') {
            const width = texture.width * spriteScale;
            const height = texture.height * spriteScale;
            slot.hitArea = new PIXI.Rectangle(-width / 2, -height / 2, width, height);
        }
    }

    setName(text, item, alpha) {
        if (!text) return;
        text.text = item?.deckName || item?.name || '';
        text.alpha = alpha;
        text.visible = Boolean(item);
    }

    setDescription(text, item, alpha) {
        if (!text) return;
        text.text = item?.description || '';
        text.alpha = alpha;
        text.visible = Boolean(item?.description);
    }

    placeDecks() {
        const count = this.items.length;
        this.setSlot(this.centerDeck, this.itemAt(0), 0, 1, 1, 2);
        this.setSlot(this.leftDeck, count > 1 ? this.itemAt(-1) : null, -SIDE_X, SIDE_SCALE, SIDE_ALPHA, 1);
        this.setSlot(this.rightDeck, count > 1 ? this.itemAt(1) : null, SIDE_X, SIDE_SCALE, SIDE_ALPHA, 1);
        this.setSlot(this.prevPrevDeck, count > 1 ? this.itemAt(-2) : null, -SIDE_X * 2, SIDE_SCALE, 0, 0);
        this.setSlot(this.nextNextDeck, count > 1 ? this.itemAt(2) : null, SIDE_X * 2, SIDE_SCALE, 0, 0);

        this.setName(this.prevName, this.itemAt(-1), 0);
        this.setName(this.deckName, this.itemAt(0), 1);
        this.setName(this.nextName, this.itemAt(1), 0);
        this.setDescription(this.prevDescription, this.itemAt(-1), 0);
        this.setDescription(this.description, this.itemAt(0), 1);
        this.setDescription(this.nextDescription, this.itemAt(1), 0);
        this.updateClaimButton();
    }

    updateClaimButton() {
        if (!this.claimButton) return;

        const item = this.currentItem();
        const claiming = this.claimingItemId != null;
        const canClaim = Boolean(item?.canClaim) && !claiming;
        if (this.claimLabel) {
            this.claimLabel.text = claiming ? 'CLAIMING...' : 'CLAIM';
        }
        this.claimButton.fill = canClaim ? this.claimFill : null;
        this.claimButton.borderFill = canClaim ? this.claimBorderFill : null;
        this.claimButton.outerBorderColor = canClaim ? '#60350a' : '#41557c';
        this.claimButton.backgroundColor = '#4a5f82';
        this.claimButton.borderColor = '#41557c';
        this.claimButton.drawButton();
        this.claimButton.onButtonTap = canClaim
            ? () => this.onClaimItem?.(item.id)
            : null;
    }

    buildName(width, height) {
        const titleGradient = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#ffe17d' },
                { offset: 0.3, color: '#ffe17d' },
                { offset: 1, color: '#faf2d8' },
            ],
        });
        const text = new PIXI.Text({
            text: '',
            style: {
                fontFamily: 'Caladea',
                fontSize: 70,
                fontWeight: 'bold',
                fill: titleGradient,
                align: 'center',
                stroke: { color: '#422400', width: 5 },
                wordWrap: true,
                wordWrapWidth: 1000,
            },
        });
        text.anchor.set(0.5);
        text.x = width / 2;
        text.y = height * 0.72;
        text.eventMode = 'none';
        this.addChild(text);
        return text;
    }

    buildDescription(width, height) {
        const text = new PIXI.Text({
            text: '',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 40,
                fill: '#d7deea',
                align: 'center',
                wordWrap: true,
                wordWrapWidth: 1000,
                lineHeight: 50,
            },
        });
        text.anchor.set(0.5);
        text.x = width / 2;
        text.y = height * 0.775;
        text.eventMode = 'none';
        this.addChild(text);
        return text;
    }

    buildDeckSlot(name, onTap) {
        const slot = onTap ? new Tappable() : new PIXI.Container();
        slot.label = name;
        if (onTap) slot.onTap = onTap;
        else slot.eventMode = 'none';
        this.carousel.addChild(slot);
        return slot;
    }

    buildMainLayout(width, height) {
        this.layoutVersion += 1;
        this.slideTimeline?.kill();
        this.slideTimeline = null;
        this.isAnimating = false;
        this.removeChildren();

        this.hitArea = new PIXI.Rectangle(0, 0, width, height);

        this.background = new PIXI.Graphics();
        this.background.label = 'starterDeckFade';
        this.background.rect(0, 0, width, height);
        this.background.fill({ color: '#000000' });
        this.background.alpha = 0.9;
        this.background.eventMode = 'none';
        this.addChild(this.background);

        this.title = new PIXI.Text({
            text: 'SELECT A\nSTARTER DECK',
            style: {
                fontFamily: 'Open Sans',
                fontSize: 60,
                fontWeight: '700',
                fill: '#ffffff',
                align: 'center',
            },
        });
        this.title.anchor.set(0.5);
        this.title.x = width / 2;
        this.title.y = 320;
        this.title.eventMode = 'none';
        this.addChild(this.title);

        this.carousel = new PIXI.Container();
        this.carousel.label = 'starterDeckCarousel';
        this.carousel.x = width / 2;
        this.carousel.y = height * 0.44;
        this.carousel.sortableChildren = true;
        this.addChild(this.carousel);

        this.prevPrevDeck = this.buildDeckSlot('prevPrevDeck');
        this.nextNextDeck = this.buildDeckSlot('nextNextDeck');
        this.leftDeck = this.buildDeckSlot('leftDeck', () => this.slide(-1));
        this.rightDeck = this.buildDeckSlot('rightDeck', () => this.slide(1));
        this.centerDeck = this.buildDeckSlot('centerDeck');

        this.prevName = this.buildName(width, height);
        this.nextName = this.buildName(width, height);
        this.deckName = this.buildName(width, height);
        this.prevDescription = this.buildDescription(width, height);
        this.nextDescription = this.buildDescription(width, height);
        this.description = this.buildDescription(width, height);

        this.claimFill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#563307' },
                { offset: 1, color: '#986710' },
            ],
        });
        this.claimBorderFill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#a86212' },
                { offset: 1, color: '#dea01c' },
            ],
        });
        const claimFontFill = new PIXI.FillGradient({
            type: 'linear',
            start: { x: 0, y: 0 },
            end: { x: 0, y: 1 },
            colorStops: [
                { offset: 0, color: '#ffe17d' },
                { offset: 0.3, color: '#ffe17d' },
                { offset: 1, color: '#faf2d8' },
            ],
        });
        this.claimButton = new TappableGraphicsButton({
            text: 'CLAIM',
            width: BUTTON_WIDTH,
            height: BUTTON_HEIGHT,
            cornerRadius: 40,
            fill: this.claimFill,
            borderFill: this.claimBorderFill,
            outerBorderColor: '#60350a',
            fontFill: claimFontFill,
            fontStrokeColor: '#422400',
            fontStrokeWidth: 8,
            fontSize: 48,
            fontFamily: 'Caladea',
        });
        this.claimButton.x = (width - BUTTON_WIDTH) / 2;
        this.claimButton.y = height * 0.84;
        this.claimLabel = findText(this.claimButton);
        this.addChild(this.claimButton);

        this.placeDecks();
    }
}
